import type { Options } from 'highcharts';
import type { IIdName } from '@/interfaces/general/IIdName.ts';
import type { IAssetWithChart, IOverviewChartDto } from '@/interfaces/storageForecasting/IRunOutput.ts';
import type { IAssetGroup } from '@/interfaces/storageForecasting/IAssetGroup.ts';
import type { IAssetRunFilters } from '@/interfaces/storageForecasting/IAssetRunFilters.ts';
import type { IAssetInputDto } from '@/interfaces/storageForecasting/IAsset.ts';
import type { IRunListItem } from '@/interfaces/storageForecasting/IRunListItem.ts';
import { DEFAULT_PERCENTILES } from '@/constants/forecasting/assetConstants.ts';
import {
    buildStorecastMarginOverlayChartOptions,
    marginTotalsFromOverviewEntry,
    type IMarginOverlaySeriesInput,
    type IStorecastOutputFilters,
} from '@/utils/storageForecasting/assetUtils.ts';

export interface IAssetRunSelection {
    assetId: string;
    assetLabel: string;
    runId: string;
}

function uniqueById (options: Array<IIdName>): Array<IIdName> {
    const seen = new Set<string>();
    return options.filter((option) => {
        if (seen.has(option.id)) return false;
        seen.add(option.id);
        return true;
    });
}

function getAssetGroupInputs (
    assetGroup: IAssetGroup | null | undefined,
    inputs: ReadonlyArray<IAssetInputDto>,
): Array<IAssetInputDto> {
    if (!assetGroup) return [];
    const inputById = new Map(inputs.map(input => [input.id, input]));
    return assetGroup.assetIds
        .map(id => inputById.get(id))
        .filter((input): input is IAssetInputDto => !!input);
}

function getAssetForecastQuarterOptions (inputs: ReadonlyArray<IAssetInputDto>): Array<IIdName> {
    const quarters = inputs.flatMap(input => input.runs.map(run => run.quarter));
    return uniqueById(quarters.sort().map(quarter => ({ id: quarter, name: quarter })));
}

function getAssetForecastScenarioOptions (
    inputs: ReadonlyArray<IAssetInputDto>,
    quarter: string | null,
): Array<IIdName> {
    if (!quarter) return [];
    const scenarios = inputs.flatMap(input => input.runs
        .filter(run => run.quarter === quarter)
        .map(run => run.scenario));
    return uniqueById(scenarios.sort().map(scenario => ({ id: scenario, name: scenario })));
}

function getAssetForecastPercentileOptions (): Array<IIdName> {
    return DEFAULT_PERCENTILES.map(option => ({ ...option }));
}

function getAssetForecastPercentileName (percentile: string | null | undefined): string {
    const percentileId = percentile ?? 'mean';
    return DEFAULT_PERCENTILES.find(option => option.id === percentileId)?.name ?? percentileId;
}

function getDefaultAssetRunFilters (
    inputs: ReadonlyArray<IAssetInputDto>,
    preferredFilters?: Partial<IAssetRunFilters> | null,
): IAssetRunFilters {
    const quarterOptions = getAssetForecastQuarterOptions(inputs);
    const quarter = preferredFilters?.quarter && quarterOptions.some(option => option.id === preferredFilters.quarter)
        ? preferredFilters.quarter
        : quarterOptions[quarterOptions.length - 1]?.id ?? null;

    const scenarioOptions = getAssetForecastScenarioOptions(inputs, quarter);
    const scenario = preferredFilters?.scenario && scenarioOptions.some(option => option.id === preferredFilters.scenario)
        ? preferredFilters.scenario
        : scenarioOptions[0]?.id ?? null;

    const percentileOptions = getAssetForecastPercentileOptions();
    const percentile = preferredFilters?.percentile && percentileOptions.some(option => option.id === preferredFilters.percentile)
        ? preferredFilters.percentile
        : percentileOptions[0]?.id ?? 'mean';

    return {
        quarter,
        scenario,
        percentile,
    };
}

function getRunIdForFilters (
    runListItem: IRunListItem | null | undefined,
    filters: IAssetRunFilters,
): string | null {
    if (!runListItem || !filters.quarter || !filters.scenario) return null;
    return runListItem.quarterToScenarios[filters.quarter]?.scenarios
        .find(scenario => scenario.name === filters.scenario || scenario.id === filters.scenario)
        ?.id ?? null;
}

function getAssetForecastSelectedRuns (
    inputs: ReadonlyArray<IAssetInputDto>,
    filters: IAssetRunFilters,
): Array<IAssetRunSelection> {
    if (!filters.quarter || !filters.scenario) return [];
    return inputs
        .map((input): IAssetRunSelection | null => {
            const run = input.runs.find(item => item.quarter === filters.quarter
                && item.scenario === filters.scenario
                && (item.isCompleted || item.runState === 'Completed'));
            if (!run) return null;
            return {
                assetId: input.id,
                assetLabel: input.data.name,
                runId: run.id,
            };
        })
        .filter((selection): selection is IAssetRunSelection => !!selection);
}

function toStorecastOutputFilters (
    assetGroup: IAssetGroup,
    filters: IAssetRunFilters,
): IStorecastOutputFilters {
    return {
        percentile: filters.percentile ?? 'mean',
        period: assetGroup.options.period,
        yearStarting: assetGroup.options.yearStarting,
    };
}

function buildAssetComparisonChartData (
    assetGroup: IAssetGroup,
    inputs: ReadonlyArray<IAssetInputDto>,
    overviewChart: IOverviewChartDto,
    filters: IAssetRunFilters,
    darkMode: boolean,
    chartColours: string[],
): IAssetWithChart | null {
    const outputFilters = toStorecastOutputFilters(assetGroup, filters);
    const runSelections = getAssetForecastSelectedRuns(inputs, filters);

    const seriesInputs: Array<IMarginOverlaySeriesInput> = runSelections
        .map((selection, index): IMarginOverlaySeriesInput | null => {
            const totals = marginTotalsFromOverviewEntry(overviewChart[index], outputFilters);
            if (!totals) return null;
            return {
                assetLabel: selection.assetLabel,
                categories: totals.categories,
                totals: totals.totals,
                yAxisUnit: totals.yAxisUnit,
            };
        })
        .filter((input): input is IMarginOverlaySeriesInput => !!input);

    if (!seriesInputs.length) return null;

    const chartOptions: Options = {
        ...buildStorecastMarginOverlayChartOptions(seriesInputs, darkMode, chartColours),
        title: {
            text: 'Margin',
        },
    };

    return {
        chart: chartOptions,
        description: [],
        yAxis: { min: 0, max: 0 },
        metricByYear: {},
    };
}

export {
    buildAssetComparisonChartData,
    getAssetForecastPercentileName,
    getAssetForecastPercentileOptions,
    getAssetForecastQuarterOptions,
    getAssetForecastScenarioOptions,
    getAssetForecastSelectedRuns,
    getAssetGroupInputs,
    getDefaultAssetRunFilters,
    getRunIdForFilters,
};
